import React from 'react';
import { CheckCircle2, Clock, FlaskConical, Grid3x3, ChevronDown, Crown, Gem, EyeOff, Shield, Sparkles, Zap, Award } from 'lucide-react';
import { cn } from '@/lib/utils';
import { UserTypeFilter } from '@/hooks/usePaginatedMediaData';
import { useUserTypeCounts } from '@/hooks/useUserTypeCounts';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface UserTypeFilterBarProps {
  value: UserTypeFilter;
  onChange: (filter: UserTypeFilter) => void;
  className?: string;
}

// Ordem: Roles → Planos → Status → Admin → Teste → Todos
const filterOptions = [
  {
    value: 'sponsors' as UserTypeFilter,
    label: 'Patrocinadores',
    icon: Crown,
    description: 'Usuários sponsors',
    iconColor: 'text-amber-400'
  },
  {
    value: 'sponsored' as UserTypeFilter,
    label: 'Patrocinadas',
    icon: Gem,
    description: 'Usuários sponsored',
    iconColor: 'text-pink-400'
  },
  {
    value: 'free' as UserTypeFilter,
    label: 'Plano Free',
    icon: Sparkles,
    description: 'Planos gratuitos',
    iconColor: 'text-gray-400'
  },
  {
    value: 'standard' as UserTypeFilter,
    label: 'Plano Standard',
    icon: Zap,
    description: 'Plano intermediário',
    iconColor: 'text-blue-400'
  },
  {
    value: 'premium' as UserTypeFilter,
    label: 'Plano Premium',
    icon: Award,
    description: 'Planos premium',
    iconColor: 'text-purple-400'
  },
  {
    value: 'active' as UserTypeFilter,
    label: 'Perfis Ativos',
    icon: CheckCircle2,
    description: 'Onboarding completo',
    iconColor: 'text-emerald-400'
  },
  {
    value: 'pending' as UserTypeFilter,
    label: 'Perfis Pendentes',
    icon: Clock,
    description: 'Onboarding pendente',
    iconColor: 'text-amber-400'
  },
  {
    value: 'hidden' as UserTypeFilter,
    label: 'Ocultos',
    icon: EyeOff,
    description: 'Perfis ocultos',
    iconColor: 'text-gray-400'
  },
  {
    value: 'admins' as UserTypeFilter,
    label: 'Moderadores e Administradores',
    icon: Shield,
    description: 'Equipe administrativa',
    iconColor: 'text-blue-400'
  },
  {
    value: 'test' as UserTypeFilter,
    label: 'Contas de Teste',
    icon: FlaskConical,
    description: 'Usuários test-*',
    iconColor: 'text-purple-400'
  },
  {
    value: 'all' as UserTypeFilter,
    label: 'Todos os Usuários',
    icon: Grid3x3,
    description: 'Todos (exceto teste)',
    iconColor: 'text-blue-400'
  },
];

export const UserTypeFilterBar: React.FC<UserTypeFilterBarProps> = ({
  value,
  onChange,
  className
}) => {
  const { data: counts } = useUserTypeCounts();

  const formatCount = (count?: number) => {
    if (!count) return '0';
    if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
    return count.toString();
  };

  const selectedOption = filterOptions.find(opt => opt.value === value) || filterOptions[0];
  const SelectedIcon = selectedOption.icon;

  return (
    <div 
      className={cn(
        "bg-gradient-to-r from-black/90 to-black/80 backdrop-blur-xl border-t border-white/10 px-4 py-2",
        className
      )}
    >
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger 
          className={cn(
            "w-full h-auto flex items-center justify-between px-4 py-2.5",
            "bg-black/90 backdrop-blur-sm",
            "border border-white/20 rounded-lg",
            "text-white font-medium transition-all duration-300",
            "hover:border-luxury-gold/50 hover:shadow-lg hover:shadow-luxury-gold/20 hover:bg-black",
            "focus:ring-2 focus:ring-luxury-gold/30 focus:border-luxury-gold/50",
            "data-[state=open]:bg-black data-[state=open]:border-luxury-gold/50"
          )}
          aria-label="Filtrar tipo de usuário"
        >
          <div className="flex items-center gap-2.5 flex-1">
            <SelectedIcon className={cn("w-4 h-4", selectedOption.iconColor)} />
            <span className="text-sm text-white">{selectedOption.label}</span>
          </div>
          <div className="flex items-center gap-2.5">
            {counts?.[value as keyof typeof counts] !== undefined && (
              <span className="px-2.5 py-0.5 bg-white/20 rounded-full text-xs font-semibold text-white">
                {formatCount(counts[value as keyof typeof counts])}
              </span>
            )}
            <ChevronDown className="w-4 h-4 text-white/70" />
          </div>
        </SelectTrigger>
        
        <SelectContent 
          className={cn(
            "bg-black border border-white/20",
            "rounded-lg shadow-2xl z-[70] min-w-[var(--radix-select-trigger-width)]"
          )}
          position="popper"
          sideOffset={8}
        >
          {filterOptions.map((option) => {
            const Icon = option.icon;
            const count = counts?.[option.value as keyof typeof counts];
            const isActive = value === option.value;
            
            return (
              <SelectItem
                key={option.value}
                value={option.value}
                className={cn(
                  "flex items-center justify-between px-6 py-4 cursor-pointer",
                  "text-white hover:bg-luxury-gold/10 hover:text-white",
                  "transition-all duration-200 focus:bg-luxury-gold/10 focus:text-white",
                  "data-[highlighted]:bg-luxury-gold/10 data-[highlighted]:text-white",
                  isActive && "bg-gradient-to-r from-luxury-gold/20 to-luxury-red/10 border-l-4 border-luxury-gold text-white font-semibold"
                )}
              >
                <div className="flex items-center gap-3 flex-1">
                  <Icon className={cn("w-5 h-5", isActive ? option.iconColor : "text-white/60")} />
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-white">{option.label}</span>
                      {count !== undefined && (
                        <span className={cn(
                          "px-2 py-0.5 rounded-full text-xs font-semibold",
                          isActive ? "bg-luxury-gold/30 text-white" : "bg-white/10 text-white"
                        )}>
                          {formatCount(count)}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-white/60">{option.description}</span>
                  </div>
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
};
